import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import './LoginForm.css'

function ForgotPasswordForm() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [localError, setLocalError] = useState('')
  const [showReset, setShowReset] = useState(false)
  const [resetData, setResetData] = useState({
    confirmationCode: '',
    newPassword: '',
    confirmPassword: ''
  })

  const { forgotPassword, resetPasswordConfirm, error, clearError } = useAuth()
  const navigate = useNavigate()

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setLocalError('')

    if (!email || !email.includes('@')) {
      setLocalError('Please enter a valid email address')
      setIsLoading(false)
      return
    }

    try {
      const result = await forgotPassword(email)
      
      if (result.success) {
        setShowReset(true)
      } else {
        setLocalError(result.error || 'Failed to send reset email')
      }
    } catch {
      setLocalError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setLocalError('')

    // Validation
    if (!resetData.confirmationCode || !resetData.newPassword || !resetData.confirmPassword) {
      setLocalError('Please fill in all fields')
      setIsLoading(false)
      return
    }

    if (resetData.newPassword !== resetData.confirmPassword) {
      setLocalError('Passwords do not match')
      setIsLoading(false)
      return
    }

    if (resetData.newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters long')
      setIsLoading(false)
      return
    }

    // Check password requirements
    const hasUpperCase = /[A-Z]/.test(resetData.newPassword)
    const hasLowerCase = /[a-z]/.test(resetData.newPassword)
    const hasNumbers = /\d/.test(resetData.newPassword)

    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setLocalError('Password must contain uppercase, lowercase, and numbers')
      setIsLoading(false)
      return
    }

    try {
      const result = await resetPasswordConfirm(
        email, 
        resetData.confirmationCode, 
        resetData.newPassword
      )
      
      if (result.success) {
        navigate('/login', { 
          state: { 
            message: 'Password reset successful! Please sign in with your new password.' 
          } 
        })
      } else {
        setLocalError(result.error || 'Failed to reset password')
      }
    } catch {
      setLocalError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetDataChange = (e) => {
    const { name, value } = e.target
    setResetData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear errors when user starts typing
    if (localError) setLocalError('')
    if (error) clearError()
  }

  const displayError = localError || error

  if (showReset) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Password</h1>
            <p>Enter the code sent to {email}</p>
          </div>

          <form onSubmit={handleResetSubmit} className="auth-form">
            {displayError && (
              <div className="error-message">
                {displayError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="confirmationCode">Confirmation Code</label>
              <input
                type="text"
                id="confirmationCode"
                name="confirmationCode"
                value={resetData.confirmationCode}
                onChange={handleResetDataChange}
                placeholder="Enter 6-digit code"
                required
                maxLength="6"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={resetData.newPassword}
                onChange={handleResetDataChange}
                placeholder="Enter new password"
                required
                autoComplete="new-password"
              />
              <div className="password-requirements">
                <small>Must be 8+ characters with uppercase, lowercase, and numbers</small>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={resetData.confirmPassword}
                onChange={handleResetDataChange}
                placeholder="Confirm new password"
                required
                autoComplete="new-password"
              />
            </div>

            <button 
              type="submit" 
              className="auth-button primary"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div className="auth-links">
            <button 
              type="button" 
              onClick={() => setShowReset(false)}
              className="link-button"
            >
              Back to email entry
            </button>
            <Link to="/login" className="link">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Reset Password</h1>
          <p>Enter your email to receive a reset code</p>
        </div>

        <form onSubmit={handleEmailSubmit} className="auth-form">
          {displayError && (
            <div className="error-message">
              {displayError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (localError) setLocalError('')
                if (error) clearError()
              }}
              placeholder="your.email@example.com"
              required
              autoComplete="email"
            />
          </div>

          <button 
            type="submit" 
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/login" className="link">
            Back to Sign In
          </Link>
          <div className="auth-divider">
            <span>Need an account? Contact a club administrator</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordForm